import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { FiMenu, FiMaximize, FiBell, FiUser, FiLogOut, FiSettings, FiSearch } from "react-icons/fi";

export default function Navbar({ onToggleSidebar }) {
  const { t } = useTranslation("common");
  const { user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-30 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
      {/* Left */}
      <div className="flex items-center gap-2">
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded hover:bg-gray-100 text-gray-600"
        >
          <FiMenu size={18} />
        </button>
        <div className="relative">
          <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t("sidebar.searchPlaceholder")}
            className="w-40 sm:w-64 bg-gray-100 text-sm rounded pl-8 pr-3 py-1.5 border border-gray-200 focus:outline-none focus:border-adminlte-info"
          />
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-1">
        {/* Notifications */}
        <button className="relative p-2 rounded hover:bg-gray-100 text-gray-600">
          <FiBell size={16} />
          <span className="absolute top-1 right-1 w-2 h-2 bg-adminlte-danger rounded-full"></span>
        </button>

        {/* Fullscreen */}
        <button
          onClick={toggleFullscreen}
          className="hidden sm:block p-2 rounded hover:bg-gray-100 text-gray-600"
        >
          <FiMaximize size={16} />
        </button>

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="p-2 rounded hover:bg-gray-100 text-gray-600"
          >
            <FiUser size={16} />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded shadow-lg border border-gray-200 py-1 z-50">
              <a
                href="/profile"
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setDropdownOpen(false)}
              >
                <FiUser size={14} />
                {t("navbar.profile")}
              </a>
              <a
                href="/preferences"
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setDropdownOpen(false)}
              >
                <FiSettings size={14} />
                {t("navbar.preferences")}
              </a>
              <hr className="my-1" />
              <button
                onClick={logout}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-adminlte-danger hover:bg-red-50"
              >
                <FiLogOut size={14} />
                {t("navbar.signOut")}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
